import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

/**
 * Image viewer with pinch-to-zoom and drag-to-pan.
 * Only the image zooms - the page stays fixed.
 */
export const ImageZoomViewer = ({ src, alt, className = '' }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lastTouchRef = useRef(null);
  const lastDistRef = useRef(null);
  const containerRef = useRef(null);

  const clampScale = (s) => Math.min(Math.max(s, 1), 5);

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  }, [scale]);

  const handleWheel = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale((s) => {
      const newScale = clampScale(s + delta);
      if (newScale <= 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastDistRef.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && scale > 1) {
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setIsDragging(true);
    }
  }, [scale]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      if (lastDistRef.current) {
        const delta = (dist - lastDistRef.current) * 0.01;
        setScale((s) => {
          const newScale = clampScale(s + delta);
          if (newScale <= 1) setPosition({ x: 0, y: 0 });
          return newScale;
        });
      }
      lastDistRef.current = dist;
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      const touch = e.touches[0];
      if (lastTouchRef.current) {
        const dx = touch.clientX - lastTouchRef.current.x;
        const dy = touch.clientY - lastTouchRef.current.y;
        setPosition((p) => ({ x: p.x + dx, y: p.y + dy }));
      }
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
    }
  }, [isDragging, scale]);

  const handleTouchEnd = useCallback(() => {
    lastDistRef.current = null;
    lastTouchRef.current = null;
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (scale > 1) {
      e.preventDefault();
      lastTouchRef.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
    }
  }, [scale]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && scale > 1 && lastTouchRef.current) {
      const dx = e.clientX - lastTouchRef.current.x;
      const dy = e.clientY - lastTouchRef.current.y;
      setPosition((p) => ({ x: p.x + dx, y: p.y + dy }));
      lastTouchRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [isDragging, scale]);

  const handleMouseUp = useCallback(() => {
    lastTouchRef.current = null;
    setIsDragging(false);
  }, []);

  const isZoomed = scale > 1;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden touch-none select-none ${className}`}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      style={{ cursor: isZoomed ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
      data-testid="image-zoom-viewer"
    >
      <motion.img
        src={src}
        alt={alt}
        className="max-w-full max-h-[80vh] object-contain rounded-lg pointer-events-none"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        }}
        draggable={false}
      />

      {/* Zoom indicator */}
      {isZoomed && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="font-body text-white text-xs">{Math.round(scale * 100)}%</span>
        </div>
      )}

      {/* Hint */}
      {!isZoomed && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full opacity-60">
          <span className="font-body text-white text-xs">Double-tap to zoom</span>
        </div>
      )}
    </div>
  );
};
